/**
 * SidebarNav -- left sidebar navigation for Tablet + Desktop.
 *
 * A3-5 (Sprint 1): restructured from flat list to 7-section grouping per
 * UX_NAVIGATION_PATTERNS S4. Tabs are bucketed into semantic sections:
 *   Core           -- map, farm, chat (always primary)
 *   Operations     -- schlagkartei, deliveries, navigation
 *   Tasks          -- my_tasks, my_jobs, my_trip, my_hours, sign_off
 *   Reports        -- reports, bank_reports, audit_trail, exports
 *   Compliance     -- legal_entities, bookings, contracts, gloez, damages
 *   Personal       -- today, my_deliveries, my_contracts, incoming_requests
 *   Help           -- help
 *
 * Unknown tabs fall into "Other" group.
 *
 * See: docs/architecture/UX_NAVIGATION_PATTERNS_V2.md S4
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const TAB_META = {
  map:       { icon: '\u{1F5FA}\uFE0F', labelKey: 'nav.map' },
  farm:      { icon: '\u{1F3E1}', labelKey: 'nav.farm' },
  chat:      { icon: '\u{1F4AC}', labelKey: 'nav.chat' },
  my_tasks:  { icon: '\u2705', labelKey: 'nav.myTasks' },
  reports:   { icon: '\u{1F4CA}', labelKey: 'nav.reports' },
  today:     { icon: '\u{1F4C5}', labelKey: 'nav.today' },
  my_hours:  { icon: '\u23F1\uFE0F', labelKey: 'nav.myHours' },
  help:      { icon: '\u2753', labelKey: 'nav.help' },
  my_jobs:   { icon: '\u{1F527}', labelKey: 'nav.myJobs' },
  my_trip:   { icon: '\u{1F69B}', labelKey: 'nav.myTrip' },
  navigation: { icon: '\u{1F9ED}', labelKey: 'nav.navigation' },
  sign_off:  { icon: '\u270D\uFE0F', labelKey: 'nav.signOff' },
  incoming_requests: { icon: '\u{1F4E5}', labelKey: 'nav.incomingRequests' },
  my_deliveries: { icon: '\u{1F4E6}', labelKey: 'nav.myDeliveries' },
  my_contracts: { icon: '\u{1F4CB}', labelKey: 'nav.myContracts' },
  deliveries: { icon: '\u{1F4E6}', labelKey: 'nav.deliveries' },
  legal_entities: { icon: '\u{1F3DB}\uFE0F', labelKey: 'nav.legalEntities' },
  bookings:  { icon: '\u{1F4D2}', labelKey: 'nav.bookings' },
  exports:   { icon: '\u2B07\uFE0F', labelKey: 'nav.exports' },
  audit_trail: { icon: '\u{1F50D}', labelKey: 'nav.auditTrail' },
  bank_reports: { icon: '\u{1F3E6}', labelKey: 'nav.bankReports' },
  contracts: { icon: '\u{1F4CB}', labelKey: 'nav.contracts' },
  damages:   { icon: '\u26A0\uFE0F', labelKey: 'nav.damages' },
  schlagkartei: { icon: '\u{1F4CB}', labelKey: 'nav.schlagkartei' },
  gloez:     { icon: '\u{1F33F}', labelKey: 'nav.gloez' },
  psm_records: { icon: '\u{1F9EA}', labelKey: 'nav.psmRecords' },
};

// Tab -> Section mapping (7 sections per spec)
const TAB_TO_SECTION = {
  map: 'Core', farm: 'Core', chat: 'Core',
  schlagkartei: 'Operations', deliveries: 'Operations', navigation: 'Operations',
  my_tasks: 'Tasks', my_jobs: 'Tasks', my_trip: 'Tasks', my_hours: 'Tasks', sign_off: 'Tasks',
  reports: 'Reports', bank_reports: 'Reports', audit_trail: 'Reports', exports: 'Reports',
  legal_entities: 'Compliance', bookings: 'Compliance', contracts: 'Compliance', gloez: 'Compliance', damages: 'Compliance', psm_records: 'Compliance',
  today: 'Personal', my_deliveries: 'Personal', my_contracts: 'Personal', incoming_requests: 'Personal',
  help: 'Help',
};

const SECTION_ORDER = ['Core', 'Operations', 'Tasks', 'Reports', 'Compliance', 'Personal', 'Help', 'Other'];

const SECTION_LABEL_KEY = {
  Core: 'sidebar.section.core',
  Operations: 'sidebar.section.operations',
  Tasks: 'sidebar.section.tasks',
  Reports: 'sidebar.section.reports',
  Compliance: 'sidebar.section.compliance',
  Personal: 'sidebar.section.personal',
  Help: 'sidebar.section.help',
  Other: 'sidebar.section.other',
};

/**
 * Group tabs into 7 sections (plus "Other" for unknown tabs).
 */
function groupTabsBySection(tabs) {
  const groups = {};
  for (const tab of tabs) {
    const section = TAB_TO_SECTION[tab] || 'Other';
    if (!groups[section]) groups[section] = [];
    groups[section].push(tab);
  }
  return groups;
}

export default function SidebarNav({ tabs = [], activeTab, onTabChange, onMore, appName }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState({});

  const sections = groupTabsBySection(tabs);

  const toggleSection = (name) => {
    setCollapsed((s) => ({ ...s, [name]: !s[name] }));
  };

  return (
    <nav
      className="flex flex-col w-[200px] flex-shrink-0 bg-[var(--aif-bg-surface)]
                 border-r border-[var(--aif-border-default)] py-3 px-3"
      aria-label={t('nav.mainNavigation', 'Main navigation')}
      data-testid="sidebar-nav"
    >
      {/* App brand */}
      <div className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wider px-3 mb-3">
        {appName || 'AI Farm'}
      </div>

      {/* Sections */}
      <div className="flex-1 flex flex-col gap-3 overflow-y-auto">
        {SECTION_ORDER.map((sectionName) => {
          const sectionTabs = sections[sectionName];
          if (!sectionTabs || sectionTabs.length === 0) return null;
          const isCollapsed = collapsed[sectionName];
          return (
            <div
              key={sectionName}
              data-testid={`sidebar-section-${sectionName.toLowerCase()}`}
            >
              <button
                type="button"
                onClick={() => toggleSection(sectionName)}
                data-testid={`sidebar-section-header-${sectionName.toLowerCase()}`}
                className="w-full flex items-center justify-between px-3 py-1 text-[10px] font-semibold uppercase tracking-wider
                           text-[var(--aif-text-muted)] bg-transparent border-none cursor-pointer text-left"
                aria-expanded={!isCollapsed}
              >
                <span>{t(SECTION_LABEL_KEY[sectionName], sectionName)}</span>
                <span aria-hidden="true" className="text-xs">{isCollapsed ? '+' : '\u2212'}</span>
              </button>
              {!isCollapsed && (
                <div className="flex flex-col gap-0.5 mt-1">
                  {sectionTabs.map((tab) => {
                    const meta = TAB_META[tab] || { icon: '\u25C9', labelKey: `nav.${tab}` };
                    const isActive = tab === activeTab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => onTabChange?.(tab)}
                        data-testid={`sidebar-tab-${tab}`}
                        data-tab={tab}
                        aria-current={isActive ? 'page' : undefined}
                        className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium transition-colors
                          ${isActive
                            ? 'bg-[var(--aif-color-primary-light,rgba(22,163,74,0.15))] text-[var(--aif-color-primary)]'
                            : 'text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-elevated)]'
                          }`}
                        style={{ minHeight: 44 }}
                      >
                        <span className="text-base leading-none">{meta.icon}</span>
                        <span>{t(meta.labelKey, tab)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mehr -- opens hamburger/drawer */}
      {onMore && (
        <button
          onClick={onMore}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium
                     text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-elevated)] transition-colors mt-2"
          data-testid="sidebar-more"
        >
          <span className="text-base leading-none">{'\u22EF'}</span>
          <span>{t('nav.more', { defaultValue: 'Mehr' })}</span>
        </button>
      )}
    </nav>
  );
}
